import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import type { MonthComparisonPoint, YearComparisonPoint } from '../../types/statistics.types';
import type { ComparisonMeta } from '../reports/Statistics/Statistics.hooks';
import { chartPalette } from './chartPalette';
import { formatCurrency } from '../../utils/formatters/money';

type DashboardMode = 'light' | 'dark';

/** Acepta datos de comparación (meses o años) con estructura flexible */
type ComparisonChartDataPoint =
  | MonthComparisonPoint
  | YearComparisonPoint
  | Record<string, unknown>;

export interface ComparisonTrendChartProps {
  data: ComparisonChartDataPoint[];
  comparisonMeta: ComparisonMeta;
  valueFormat: 'currency' | 'number';
  title: string;
  loading?: boolean;
  mode?: DashboardMode;
}

const ChartContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'mode',
})<{ mode?: DashboardMode }>(({ mode = 'dark' }) => {
  const isLight = mode === 'light';
  return {
    padding: '24px',
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    ...(isLight
      ? {
          background: '#ffffff',
          border: '1px solid #bae6fd',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          '&::before': { content: 'none' },
        }
      : {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
  };
});

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'mode',
})<{ mode?: DashboardMode }>(({ mode = 'dark' }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: mode === 'light' ? '#0f172a' : '#ffffff',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  letterSpacing: '-0.01em',
  marginBottom: '24px',
  position: 'relative',
  zIndex: 1,
}));

function formatValue(value: number, format: 'currency' | 'number'): string {
  if (format === 'currency') {
    return formatCurrency(value);
  }
  return value.toLocaleString('es-BO');
}

export const ComparisonTrendChart: React.FC<ComparisonTrendChartProps> = ({
  data,
  comparisonMeta,
  valueFormat,
  title,
  loading = false,
  mode = 'dark',
}) => {
  const isLight = mode === 'light';
  const gridStroke = isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.05)';
  const axisStroke = isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)';
  const tickFill = isLight ? '#64748b' : 'rgba(255, 255, 255, 0.6)';

  const isMonths = comparisonMeta.mode === 'months';
  const xKey = isMonths ? 'dayOfMonth' : 'monthLabel';
  const periodKeys = comparisonMeta.periodKeys;

  const periodTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    periodKeys.forEach((k) => {
      totals[k] = 0;
    });
    data.forEach((point) => {
      periodKeys.forEach((k) => {
        totals[k] = (totals[k] ?? 0) + ((point[k] as number) ?? 0);
      });
    });
    return totals;
  }, [data, periodKeys]);

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<Record<string, unknown>>;
  }) => {
    if (!active || !payload?.length) return null;
    const point = (payload[0]?.payload ?? payload[0]) as Record<string, unknown>;
    const xLabel = isMonths ? `Día ${point.dayOfMonth}` : String(point.monthLabel ?? point.month);
    return (
      <Box
        sx={
          isLight
            ? {
                background: '#ffffff',
                border: '1px solid #e0f2fe',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }
            : {
                background:
                  'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
              }
        }
      >
        <Typography
          sx={{ fontSize: '12px', fontWeight: 600, color: isLight ? '#64748b' : '#ffffff', mb: 1 }}
        >
          {xLabel}
        </Typography>
        {comparisonMeta.periodLabels.map((label, i) => {
          const key = periodKeys[i];
          const val = (point[key] as number) ?? 0;
          const color = chartPalette.series[i % chartPalette.series.length];
          return (
            <Typography key={key} sx={{ fontSize: '13px', color, fontWeight: 600 }}>
              {label}: {formatValue(val, valueFormat)}
            </Typography>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <ChartContainer mode={mode}>
        <Box
          sx={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            Cargando...
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  if (!data?.length) {
    return (
      <ChartContainer mode={mode}>
        <Title mode={mode}>{title}</Title>
        <Box
          sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            No hay datos para comparar
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer mode={mode}>
      <Title mode={mode}>{title}</Title>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, position: 'relative', zIndex: 1 }}
      >
        {periodKeys.map((key, i) => {
          const total = periodTotals[key] ?? 0;
          const color = chartPalette.series[i % chartPalette.series.length];
          const label = comparisonMeta.periodLabels[i];
          return (
            <Box
              key={key}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                bgcolor: isLight ? 'grey.50' : 'rgba(255,255,255,0.05)',
                border: '1px solid',
                borderColor: isLight ? 'grey.200' : 'rgba(255,255,255,0.1)',
              }}
            >
              <Typography sx={{ fontSize: '11px', color: tickFill, fontWeight: 500 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color }}>
                {formatValue(total, valueFormat)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis
              dataKey={xKey}
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              tickFormatter={(v) => (isMonths ? `Día ${v}` : String(v))}
            />
            <YAxis
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              tickFormatter={(v) => formatValue(Number(v), valueFormat)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ position: 'relative' }}
              formatter={(value) => (
                <span style={{ color: tickFill, fontSize: '12px' }}>{value}</span>
              )}
            />
            {periodKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={comparisonMeta.periodLabels[i]}
                stroke={chartPalette.series[i % chartPalette.series.length]}
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 1 }}
                activeDot={{ r: 5 }}
                animationDuration={800}
                animationEasing="ease-out"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </ChartContainer>
  );
};
